import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  StyledExpenses,
  StyledExpensesMoreButton,
  StyledExpensesContainer,
  StyledExpensesEmpty
} from '../Expenses/StyledExpenses';
import Table from '../../../Table';
import Spin from '../../../Spin';
import {ExpensesIcon} from '../../../../icons';
import {useFeatureAvailable, useIsCompanyChanged} from '../../../../hooks';
import {ReactComponent as EmptySvgImage} from '../../../../static/images/pages/overview/empty-expenses.svg';
import {amountHelpers, dateHelpers, invoicesHelpers} from '../../../../utils/helpers';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import overviewShared from '../OverviewShared/overviewShared';
import {companyFeaturesConstants} from '../../../../constants';

const columnProps = { width: 'calc(100% / 5)' };


const tableKeys = {
  NEED_YOUR_APPROVAL: 'need-your-approval',
  RECENT_ACTIVITY: 'recent-activity',
}

const { NEED_YOUR_APPROVAL, RECENT_ACTIVITY } = tableKeys;


const Invoices = ({ ...rest }) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [approvalTableProps, setApprovalTableProps] = useState(overviewShared.defaultTableProps);
  const [activityTableProps, setActivityTableProps] = useState(overviewShared.defaultTableProps);
  const navigate = useNavigate();

  const companyId = useIsCompanyChanged();

  const dispatch = useDispatch();

  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const isBankingDisabled = useFeatureAvailable(companyFeaturesConstants.TREEZOR_DISABLED);

  const {employeeEmail} = useSelector(({ user: { employee }}) => ({ employeeEmail: employee?.email }));

  const isEmpty = useMemo(() => {
    const noData = !approvalTableProps.data.length && !activityTableProps.data.length;
    const notLoading = !approvalTableProps.loading && !activityTableProps.loading;
    return noData && notLoading;
  }, [approvalTableProps, activityTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnableLoading = useMemo(() => {
    const noData = !approvalTableProps.data.length && !activityTableProps.data.length;
    const loading = approvalTableProps.loading || activityTableProps.loading;
    return noData && loading;
  }, [approvalTableProps, activityTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQueryParams = (key) => {
    const defaultParams = {
      per_page: 3,
      sort_by: 'date',
      sort_order: 'desc',
    };
    return {
      [NEED_YOUR_APPROVAL]: {
        ...defaultParams,
        active_approver: employeeEmail,
      },
      [RECENT_ACTIVITY]: {
        ...defaultParams,
      },
    }[key] || {};
  };

  const loadTableData = overviewShared.createLoadTableData(({ query, successCallback, errorCallback }) => {
    dispatch(invoicesActions.getInvoices({ query, successCallback, errorCallback }));
  }, getQueryParams, (data) => data?.invoices || []);

  useEffect(() => {
    if (!companyId) return;

    loadTableData({key: RECENT_ACTIVITY, setter: setActivityTableProps, initial: true});
    loadTableData({key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps, initial: true});
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = (key, cursor) => {
    const setter = {
      [NEED_YOUR_APPROVAL]: setApprovalTableProps,
      [RECENT_ACTIVITY]: setActivityTableProps,
    }[key] || undefined
    if (!setter) return;

    loadTableData({key, setter, query: { cursor }})
  }

  const getLabel = overviewShared.getLabelFactory(t, ExpensesIcon);

  const handleRowClick = (record) => {
    navigate(invoicesHelpers.getInvoicePaymentDetailsLink(record.id));
  }

  const columns = [
    {
      ...columnProps,
      dataIndex: 'date',
      title: `${t('invoice')} ${t('date')}`,
      render: (value) => value ? dateHelpers.getDateWithMonth(value) : '',
    },
    {
      ...columnProps,
      dataIndex: 'vendor_local_name',
      render: (_, record) => invoicesHelpers.getInvoiceVendorName(record),
      title: t('vendor')
    },
    {
      ...columnProps,
      dataIndex: 'uploaded_by',
      title: t('uploadedBy')
    },
    {
      ...columnProps,
      dataIndex: 'payment_status',
      render: invoicesHelpers.getInvoiceStatusMark,
      title: t('status')
    },
    (isBudgetsAvailable && isBankingDisabled) && {
      dataIndex: 'approval_status',
      render: (_, record) => invoicesHelpers.getInvoiceApprovalStatusTag(record, t),
      title: `${t('approval')} ${t('status')}`
    },
    {
      ...columnProps,
      align: 'right',
      dataIndex: 'amount',
      render: (amount, record) => (
        <span className='amount'>
          {amount ? amountHelpers.getAmountWithCurrencyCode(amount, undefined, record?.currency) : ''}
        </span>
      ),
      title: t('amount'),
    },
  ];

  const getTable = ({key, tableProps, showSeeMoreButton = true} = {}) => overviewShared.getTableJSX({
    TableComponent: Table,
    columns,
    tableProps,
    onRowClick: handleRowClick,
    SeeMoreButton: StyledExpensesMoreButton,
    handleLoadMore,
    keyName: key,
    t,
    showSeeMore: showSeeMoreButton
  });

  const items = [
    approvalTableProps.data.length > 0 && {
      children: getTable({key: NEED_YOUR_APPROVAL, tableProps: approvalTableProps}),
      key: NEED_YOUR_APPROVAL,
      label: getLabel('needYourApproval'),
    },
    activityTableProps.data.length > 0 && {
      children: getTable({key: RECENT_ACTIVITY, tableProps: activityTableProps}),
      key: RECENT_ACTIVITY,
      label: getLabel('recentActivity'),
    },
  ].filter(Boolean);

  return (
    <Spin size='small' spinning={isEnableLoading}>
      <StyledExpensesContainer>
        <StyledExpenses ghost items={items} defaultActiveKey={Object.values(tableKeys)} {...rest} />

        {isEmpty && (
          <StyledExpensesEmpty
            image={<EmptySvgImage />}
            description={t('overviewPage.invoices.empty.description')}
            title={t('overviewPage.invoices.empty.title')}
          />
        )}

      </StyledExpensesContainer>
    </Spin>
  );
}

export default Invoices;
